/*
 * Copyright (c) CIRROM 2023.
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import SaveAltOutlinedIcon from "@mui/icons-material/SaveAltOutlined";
import {
  ContactsTimeline,
  getContactStatsOverTime,
  getIntegratedSystemDetail,
  getPendingChangesSummary,
  getScoreStats,
  IntegratedSystem,
  IntegratedSystemDetail,
  PendingChangesSummary,
} from "../../api/integratedsystems";

import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import Box from "@mui/material/Box";
import { toPng } from "html-to-image";
import IconButton from "@mui/material/IconButton";
import classes from "./NewCRMPanel.module.css";

import { ConfirmationNumber, HelpOutlined, Person } from "@mui/icons-material";
import { NUMBER_FORMATTER } from "../utils/utils";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import AccountBoxIcon from "@mui/icons-material/AccountBox";
import Tooltip from "@mui/material/Tooltip";
import { deepOrange, teal } from "@mui/material/colors";
import BusinessIcon from "@mui/icons-material/Business";
import DraftsIcon from "@mui/icons-material/Drafts";
import EmailIcon from "@mui/icons-material/Email";
import { getWrittenChangesMetrics } from "../../api/suggestedchanges";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import { CrmHealthScore } from "./CrmHealthScore";
import { CRMChangeStats } from "./CRMChangeStats";
import { ContactsMap } from "./ContactsMap";
import { AllTicketBoxes } from "./TicketBoxes";
import { SkeletonCRMPanel } from "./SkeletonCRMPanel";
import { isNull } from "lodash";
import { GridLegacy } from "@mui/material";

function CRMOverviewListItem(
  props: Readonly<{
    fact: number;
    header: string;
    tooltip: string;
    icon: any;
    iconColor?: string;
  }>
) {
  return (
    <ListItem disableGutters>
      <div style={{ display: "flex" }}>
        <div
          style={{
            width: "30px",
            height: "30px",
            marginRight: "5px",
            alignSelf: "center",
            color: props.iconColor,
          }}
        >
          {props.icon}
        </div>

        <Divider orientation="vertical" flexItem />

        <div
          style={{
            display: "flex",
            paddingLeft: "10px",
            gap: "10px",
          }}
        >
          <div>
            <Typography variant={"h5"} style={{ fontWeight: "bold" }}>
              {props.header}
            </Typography>
            <Typography
              variant={"h5"}
              style={{ fontWeight: "bold", color: "#777776" }}
            >
              {NUMBER_FORMATTER.format(props.fact)}
            </Typography>
          </div>

          <Tooltip title={props.tooltip}>
            <HelpOutlined style={{ width: "15px", height: "15px" }} />
          </Tooltip>
        </div>
      </div>
    </ListItem>
  );
}

export function formatWrittenChanges(index, formattedChangeStats: any[], item) {
  if (
    index > 0 &&
    formattedChangeStats[formattedChangeStats.length - 1].month === item.month
  ) {
    formattedChangeStats[formattedChangeStats.length - 1][
      "manual_approved_count"
    ] = item.manual_approved_count;
    formattedChangeStats[formattedChangeStats.length - 1]["approved_total"] +=
      item.approved_total;
    formattedChangeStats[formattedChangeStats.length - 1][
      "manual_rejected_count"
    ] = item.manual_rejected_count;
    formattedChangeStats[formattedChangeStats.length - 1]["rejected_total"] +=
      item.rejected_total;
  } else {
    formattedChangeStats.push({
      month: item.month,
      readableMonth: new Date(item.month).toLocaleDateString("en-us", {
        year: "2-digit",
        month: "short",
      }),
      manual_approved_count: item.manual_approved_count,
      approved_total: item.approved_total,
      manual_rejected_count: item.manual_rejected_count,
      rejected_total: item.rejected_total,
    });
  }
}

function CRMOverviewPanel(
  props: Readonly<{ systemDetails: IntegratedSystemDetail }>
) {
  return (
    <div className={classes.crmPanelCRMOverviewArea}>
      <Typography variant={"h3"} style={{ fontWeight: "bold" }}>
        CRM Overview
      </Typography>
      <div style={{ overflowX: "hidden", height: "calc(100% - 15px)" }}>
        <List>
          <CRMOverviewListItem
            fact={props.systemDetails.crm_stats.total_contacts}
            header={"Total contacts"}
            tooltip={"Total contacts available in your CRM"}
            icon={<AccountBoxIcon style={{ width: "30px", height: "30px" }} />}
          />
          <CRMOverviewListItem
            fact={
              props.systemDetails.crm_stats.total_contacts > 0
                ? props.systemDetails.crm_stats.total_contacts -
                  props.systemDetails.merged_contacts.tickets
                : 0
            }
            header={"Unique Contacts"}
            tooltip={"Total Contacts less duplicates"}
            icon={<AccountBoxIcon style={{ width: "30px", height: "30px" }} />}
            iconColor={teal[800]}
          />
          <CRMOverviewListItem
            fact={props.systemDetails.crm_stats.orphaned_contacts}
            header={"Orphaned Contacts"}
            tooltip={"Contacts in your CRM that have no Account"}
            icon={<AccountBoxIcon style={{ width: "30px", height: "30px" }} />}
            iconColor={deepOrange[600]}
          />
          <CRMOverviewListItem
            fact={props.systemDetails.crm_stats.companies_count.crm}
            header={"Total Companies"}
            tooltip={"Total Accounts available in your CRM"}
            icon={<BusinessIcon style={{ width: "30px", height: "30px" }} />}
          />
          <CRMOverviewListItem
            fact={props.systemDetails.crm_stats.companies_count.cirrom}
            header={"Unique Companies"}
            tooltip={
              "Accounts Cirrom considers to be unique. It could be lower ( eg. duplicate company names ) or higher ( eg. multiple domains associated with the same account ) than the CRM account number"
            }
            icon={<BusinessIcon style={{ width: "30px", height: "30px" }} />}
            iconColor={teal[800]}
          />
          {props.systemDetails.email_stats &&
          props.systemDetails.email_stats.sent_emails > 0 ? (
            <CRMOverviewListItem
              fact={props.systemDetails.email_stats.sent_emails}
              header={"Sent Email"}
              tooltip={"Sent emails in the last 90 days"}
              icon={<EmailIcon style={{ width: "30px", height: "30px" }} />}
            />
          ) : null}
          {props.systemDetails.email_stats &&
          props.systemDetails.email_stats.opened_emails > 0 ? (
            <CRMOverviewListItem
              fact={props.systemDetails.email_stats.opened_emails}
              header={"Opened Email"}
              tooltip={"Opened emails in the last 90 days"}
              icon={<DraftsIcon style={{ width: "30px", height: "30px" }} />}
              iconColor={teal[800]}
            />
          ) : null}
        </List>
      </div>
    </div>
  );
}

export function CRMPanel(props: {
  system?: IntegratedSystem;
  systemDetail?: IntegratedSystemDetail;
  dataProjectId?: number;
}) {
  const { system, systemDetail, dataProjectId } = props;
  const [pendingChangesSummary, setPendingChangesSummary] =
    useState<PendingChangesSummary>();

  const ref = useRef<HTMLDivElement>(null);

  const [systemDetails, setSystemDetails] =
    useState<IntegratedSystemDetail>(undefined);
  const [changesStats, setChangesStats] = useState<any>([]);
  const [scoreStats, setScoreStats] = useState<any>([]);

  const [showDistinctContacts, setShowDistinctContacts] =
    useState<boolean>(false);

  const [contactsTimeline, setContactsTimeline] = useState<ContactsTimeline[]>(
    []
  );

  useEffect(() => {
    let isMounted = true;

    if (dataProjectId || system?.id) {
      setPendingChangesSummary(null);
      getPendingChangesSummary(dataProjectId, system?.id, showDistinctContacts)
        .then((response) => {
          if (isMounted) {
            setPendingChangesSummary(response);
          }
        })
        .catch((err) => console.log(err));

      getScoreStats(system?.id, dataProjectId)
        .then((response) => setScoreStats(response))
        .catch((err) => console.log(err));
    }
  }, [dataProjectId, system, showDistinctContacts]);
  useEffect(() => {
    let isMounted = true;
    if (!dataProjectId && system) {
      getIntegratedSystemDetail(system.id)
        .then((details) => {
          if (isMounted) {
            setSystemDetails(details);
          }
        })
        .catch((err) => console.log(err));
      getWrittenChangesMetrics(system.id)
        .then((response) => {
          if (isMounted) {
            const formattedChangeStats = [];
            response.forEach((item, index) => {
              formatWrittenChanges(index, formattedChangeStats, item);
            });
            setChangesStats(formattedChangeStats.reverse());
          }
        })
        .catch((err) => console.log(err));

      getContactStatsOverTime(system.id, null)
        .then((resp) => {
          if (isMounted) {
            setContactsTimeline(resp);
          }
        })
        .catch((err) => console.log(err));
    } else {
      setSystemDetails(systemDetail);
      getWrittenChangesMetrics(null, dataProjectId)
        .then((response) => {
          if (isMounted) {
            const formattedChangeStats = [];
            response.forEach((item, index) => {
              formatWrittenChanges(index, formattedChangeStats, item);
            });
            setChangesStats(formattedChangeStats.reverse());
          }
        })
        .catch((err) => console.log(err));
      getContactStatsOverTime(null, dataProjectId)
        .then((resp) => {
          if (isMounted) {
            setContactsTimeline(resp);
          }
        })
        .catch((err) => console.log(err));
    }

    return () => {
      isMounted = false;
    };
  }, [system, systemDetail, dataProjectId]);

  const onExport = useCallback(() => {
    if (ref.current === null) {
      return;
    }

    toPng(ref.current, { cacheBust: true, backgroundColor: "#f4f6f8" })
      .then((dataUrl) => {
        const link = document.createElement("a");
        link.download = `${system?.name || systemDetail?.name}.png`;
        link.href = dataUrl;
        link.click();
      })
      .catch((err) => {
        console.log(err);
      });
  }, [system, ref]);

  return systemDetails !== undefined ? (
    <div className={classes.crmPanel} ref={ref}>
      <div className={classes.crmPanelHeader}>
        <Box style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <Typography
            color="textPrimary"
            variant="h1"
            style={{ fontWeight: "bold", overflowWrap: "anywhere" }}
          >
            {`${system?.name || systemDetail?.name || ""}`}
          </Typography>
          {!!dataProjectId && !!systemDetails?.criteria && (
            <Tooltip title={systemDetails.criteria} arrow>
              <HelpOutlined
                style={{
                  fontSize: "inherit",
                  color: "#182a4e",
                  cursor: "help",
                  width: "12px",
                  height: "12px",
                }}
              />
            </Tooltip>
          )}
        </Box>

        <Box
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "25px",
          }}
        >
          <ToggleButtonGroup
            exclusive
            color={"primary"}
            value={showDistinctContacts}
            onChange={(_event, value) => setShowDistinctContacts(value)}
          >
            <ToggleButton
              value={false}
              size={"small"}
              style={{
                padding: "0px 2px",
              }}
            >
              <ConfirmationNumber />
            </ToggleButton>
            <ToggleButton
              value={true}
              size={"small"}
              style={{
                padding: "0px 2px",
              }}
            >
              <Person />
            </ToggleButton>
          </ToggleButtonGroup>
          <IconButton onClick={() => onExport()} size="small" color={"info"}>
            <SaveAltOutlinedIcon />
          </IconButton>
        </Box>
      </div>
      <GridLegacy container spacing={2} rowSpacing={1} columns={15}>
        <AllTicketBoxes
          systemDetails={systemDetails}
          system={system}
          dataProjectId={dataProjectId}
          pendingChangesSummary={pendingChangesSummary}
          showDistinctContacts={showDistinctContacts}
        />
        <GridLegacy item xs={15} sm={15} md={12} lg={12} xl={12}>
          <GridLegacy container spacing={2} rowSpacing={1}>
            <GridLegacy item xs={15} sm={15} md={6} lg={6} xl={6}>
              <CrmHealthScore
                scoreStats={scoreStats}
                contactsTimeline={contactsTimeline}
                isDataProject={!!dataProjectId}
              />
            </GridLegacy>
            <GridLegacy item xs={15} sm={15} md={6} lg={6} xl={6}>
              <CRMChangeStats
                label="All"
                changeMetrics={changesStats}
                classes={classes.allChangesGraph}
              />
            </GridLegacy>
            <GridLegacy item xs={15} sm={15} md={6.5} lg={7.5} xl={7.5}>
              <ContactsMap systemDetails={systemDetails} />
            </GridLegacy>
            <GridLegacy item xs={15} sm={15} md={5.5} lg={4.5} xl={4.5}>
              <CRMChangeStats
                label="Automated"
                changeMetrics={changesStats.map((item) => {
                  return {
                    readableMonth: item.readableMonth,
                    approved_total:
                      item.approved_total - (item.manual_approved_count || 0),
                    manual_approved_count: item.manual_approved_count,
                    rejected_total:
                      item.rejected_total - (item.manual_rejected_count || 0),
                    manual_rejected_count: item.manual_rejected_count,
                  };
                })}
                classes={classes.autoChangesGraph}
                maxChartHeight={215}
              />
            </GridLegacy>
          </GridLegacy>
        </GridLegacy>
        <GridLegacy item xs={15} sm={15} md={3} lg={3} xl={3}>
          <GridLegacy container spacing={2} rowSpacing={1} height={"100%"}>
            <GridLegacy
              item
              xs={15}
              sm={15}
              md={15}
              lg={15}
              xl={15}
              height={"100%"}
            >
              <CRMOverviewPanel systemDetails={systemDetails} />
            </GridLegacy>
          </GridLegacy>
        </GridLegacy>
      </GridLegacy>
    </div>
  ) : (
    <SkeletonCRMPanel />
  );
}
